import React, { useEffect, useState, useContext, useCallback } from 'react';
import { styled } from '@compiled/react';

import type { MediaClientConfig } from '@atlaskit/media-core';
import { MediaImage } from '@atlaskit/media-image';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';

import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import { useMediaSSRModeAndAuthConfig } from '@confluence/fabric-media-support/entry-points/ssrMediaHelpers.ts';
import { createRendererMediaProvider } from '@confluence/fabric-media-support/entry-points/MediaProvider.ts';
import { ExternalShareContext } from '@confluence/external-share-context/entry-points/ExternalShareContext.ts';
import {
	TitleAlignmentType,
	COVERPICTUREWIDTH,
} from '@confluence/content-topper/entry-points/contentTopperConstants.ts';
import { useTitleContentPropertiesForPublishedPage } from '@confluence/content-topper/entry-points/useTitleContentPropertiesForPublishedPage.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { fg } from '@confluence/feature-gating';
import { isExperimentEnabled } from '@confluence/feature-experiments';

import type { RendererPageCoverPictureComponentProps } from '../pageCoverPictureTypes.ts';
import {
	CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_VIEW_PAGE,
	COVER_IMAGE_FIXED_WIDTH,
	COVER_IMAGE_FIXED_HEIGHT,
	SMART_SPACE_OVERVIEW_FIXED_HEIGHT_FULL_WIDTH,
} from '../pageCoverPictureConstants.ts';
import { usePageHeightWidthObserver } from '../usePageHeightWidthObserver.ts';
import { isAllowedExternalCoverPictureUrl } from '../coverPictureHelperFunctions.ts';

/* eslint-disable
@atlaskit/ui-styling-standard/no-styled,
@atlaskit/ui-styling-standard/no-exported-styles,
@atlaskit/ui-styling-standard/no-dynamic-styles,
@atlaskit/ui-styling-standard/no-unsafe-values,
@atlaskit/ui-styling-standard/no-imported-style-values,
@atlaskit/ui-styling-standard/no-classname-prop,
@atlaskit/ui-styling-standard/enforce-style-prop,
*/

type ImageWrapperProps = {
	isPagePreview: boolean;
	objectPosition: string;
	hasEmoji?: boolean;
	isSpaceOverview?: boolean;
	isRoundedCorners?: boolean;
	isEmbeddedConfluence?: boolean;
	dynamicCoverImageHeight?: number;
	presenterModeHeight?: number;
	isPresenterMode?: boolean;
	isFullWidthImageOption?: boolean;
	isFixedWidthImageOption?: boolean;
	isObjectHeaderEnabled?: boolean;
	smartSpaceOverviewHeight?: number;
};

export const ImageWrapper = styled.img<ImageWrapperProps>({
	height: ({
		dynamicCoverImageHeight,
		presenterModeHeight,
		isPresenterMode,
		isFixedWidthImageOption,
		isFullWidthImageOption,
		smartSpaceOverviewHeight,
	}) =>
		isPresenterMode
			? `${presenterModeHeight}px !important`
			: smartSpaceOverviewHeight
				? `${smartSpaceOverviewHeight}px !important`
				: isFullWidthImageOption
					? CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_VIEW_PAGE
					: isFixedWidthImageOption
						? `${COVER_IMAGE_FIXED_HEIGHT}px`
						: `${dynamicCoverImageHeight}px`,

	objectFit: 'cover',
	...({ isFixedWidthImageOption }) =>
		isFixedWidthImageOption
			? {
					width: `${COVER_IMAGE_FIXED_WIDTH}px`,
				}
			: {},
	marginTop: ({ isPagePreview, smartSpaceOverviewHeight }) =>
		smartSpaceOverviewHeight ? token('space.0') : isPagePreview ? undefined : token('space.200'),
	marginBottom: (props) =>
		props.smartSpaceOverviewHeight
			? token('space.0')
			: props.isObjectHeaderEnabled
				? token('space.0')
				: props.isSpaceOverview && props.hasEmoji
					? token('space.200')
					: props.isPagePreview
						? token('space.0')
						: token('space.400'),
	width: '100%',
	display: 'flex',
	objectPosition: ({ objectPosition }) => objectPosition,
	borderRadius: (props) => (props.isRoundedCorners ? token('radius.xlarge') : 'unset'),
});

type LoadingDivProps = {
	hasAllowedExternalImageLoaded?: boolean;
	isPagePreview: boolean;
	isSpaceOverview?: boolean;
	isEmbeddedConfluence?: boolean;
	isFixedWidthImageOption?: boolean;
	isObjectHeaderEnabled?: boolean;
	smartSpaceOverviewHeight?: number;
};

export const LoadingDiv = styled.div<LoadingDivProps>({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	height: ({ isFixedWidthImageOption, smartSpaceOverviewHeight }) =>
		smartSpaceOverviewHeight
			? `${smartSpaceOverviewHeight}px`
			: isFixedWidthImageOption
				? `${COVER_IMAGE_FIXED_HEIGHT}px`
				: CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_VIEW_PAGE,
	justifyContent: 'center',
	background: token('color.skeleton'),
	...({ isFixedWidthImageOption }) =>
		isFixedWidthImageOption
			? {
					marginLeft: 'var(--full-cover-image-margin, 0px)',
					maxWidth: `${COVER_IMAGE_FIXED_WIDTH}px`,
				}
			: {
					marginLeft: 'var(--full-cover-image-margin, 0px)',
				},
	marginBottom: (props) =>
		props.smartSpaceOverviewHeight
			? token('space.0')
			: props.isObjectHeaderEnabled
				? token('space.0')
				: props.isPagePreview || props.isSpaceOverview
					? token('space.200')
					: token('space.400'),
	marginTop: ({ smartSpaceOverviewHeight }) =>
		smartSpaceOverviewHeight ? token('space.0') : token('space.200'),
});

export const AllowedExternalLoadingDiv = styled.div<LoadingDivProps>({
	display: 'flex',
	height: ({ isFixedWidthImageOption, smartSpaceOverviewHeight }) =>
		smartSpaceOverviewHeight
			? `${smartSpaceOverviewHeight}px`
			: isFixedWidthImageOption
				? `${COVER_IMAGE_FIXED_HEIGHT}px`
				: CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_VIEW_PAGE,
	justifyContent: 'center',
	alignItems: 'center',
	background: token('color.skeleton'),
	width: ({ isFixedWidthImageOption, isSpaceOverview, isEmbeddedConfluence }) =>
		isFixedWidthImageOption
			? `${COVER_IMAGE_FIXED_WIDTH}px`
			: isSpaceOverview || isEmbeddedConfluence
				? '100%'
				: 'var(--full-cover-image-width, 100%)',
	visibility: (props) => (props.hasAllowedExternalImageLoaded ? 'hidden' : 'visible'),
	marginBottom: (props) =>
		props.smartSpaceOverviewHeight
			? token('space.0')
			: props.isObjectHeaderEnabled
				? token('space.0')
				: token('space.200'),
	position: 'absolute',
	marginTop: ({ smartSpaceOverviewHeight }) =>
		smartSpaceOverviewHeight ? token('space.0') : token('space.200'),
});

const AllowedExternalWrapper = styled.div({
	display: 'flex',
	justifyContent: 'center',
	borderRadius: 'unset',
});

const IdentifierWrapper = ({
	children,
	isPagePreview,
	isEmbeddedConfluence = false,
	isFixedWidthImageOption = false,
	isSmartSpaceOverview = false,
}) => (
	<div
		{...useSSRPlaceholderReplaceIdProp()}
		className="renderer-page-cover-picture"
		style={
			isPagePreview && isFixedWidthImageOption
				? {
						display: 'flex',
						justifyContent: 'center',
					}
				: !isPagePreview && !isSmartSpaceOverview
					? isFixedWidthImageOption
						? {
								marginLeft: 'var(--full-cover-image-margin, 0px)',
								width: isEmbeddedConfluence ? 'unset' : 'var(--full-cover-image-width, 100%)',
								// need to use non-tokenizable value for marginTop since space.negative.300 (-24px)
								// leaves a small gap between the nav bar and content topper
								marginTop: '-26px',
								display: isFixedWidthImageOption ? 'flex' : 'unset',
								justifyContent: isFixedWidthImageOption ? 'center' : 'unset',
							}
						: {
								marginLeft: 'var(--full-cover-image-margin, 0px)',
								width: isEmbeddedConfluence ? 'unset' : 'var(--full-cover-image-width, 100%)',
								// need to use non-tokenizable value for marginTop since space.negative.300 (-24px)
								// leaves a small gap between the nav bar and content topper
								marginTop: '-26px',
							}
					: {}
		}
	>
		{children}
	</div>
);

const getPageMode = (
	isPagePreview,
	isSpaceOverview,
	isPresenterMode,
	isEmbeddedConfluence,
	isDiffEditor,
): string => {
	if (isPagePreview) {
		return 'preview';
	} else if (isSpaceOverview) {
		return 'space-overview';
	} else if (isPresenterMode) {
		return 'presenter';
	} else if (isEmbeddedConfluence) {
		return 'embed';
	} else if (isDiffEditor) {
		return 'diff-editor';
	} else {
		return 'view';
	}
};

export const RendererPageCoverPictureComponent = ({
	contentId,
	isPagePreview,
	coverPicture,
	contentAppearance,
	isSpaceOverview,
	isSmartSpaceOverview,
	hasEmoji,
	leftRightPaddingValue,
	isPresenterMode,
	coverPictureWidthOverride,
	isDiffEditor,
	useDraftData,
}: RendererPageCoverPictureComponentProps) => {
	const { isExternalShareRequest: isExternalShare } = useContext(ExternalShareContext);
	const isEmbeddedConfluence = isEmbeddedConfluence_DO_NOT_USE();
	const [mediaConfig, setMediaConfig] = useState<{
		collection: string;
		authProvider: MediaClientConfig['authProvider'];
	} | null>(null);
	const [hasAllowedExternalImageLoaded, setHasAllowedExternalImageLoaded] =
		useState<boolean>(false);
	const ssrConfig = useMediaSSRModeAndAuthConfig({ contentId, isExternalShare });
	const isServerPlaceholder = useRenderServerPlaceholder(true);
	const isPageSSRed = isServerPlaceholder || window.__SSR_RENDERED__;

	const isMediaConfigGateReady = isPageSSRed ? !!ssrConfig : !!mediaConfig;

	const mediaConfigGated = isPageSSRed
		? ssrConfig && ssrConfig.config
		: mediaConfig && { authProvider: mediaConfig.authProvider };

	const { titleContentProperties, coverPictureWidth } = useTitleContentPropertiesForPublishedPage({
		contentId,
	});
	const isFixedWidth =
		coverPictureWidth === COVERPICTUREWIDTH.FIXED ||
		coverPictureWidthOverride === COVERPICTUREWIDTH.FIXED;
	const isFixedWidthImageOption = !isPresenterMode && isFixedWidth;

	const isFullWidth =
		coverPictureWidth === COVERPICTUREWIDTH.FULL ||
		coverPictureWidthOverride === COVERPICTUREWIDTH.FULL;
	const isFullWidthImageOption = !isPresenterMode && isFullWidth;

	const isValidCustomSitesTitleAlignment =
		titleContentProperties?.titleLayoutAlignment == TitleAlignmentType.CENTER ||
		titleContentProperties?.titleLayoutAlignment == TitleAlignmentType.LEFT;

	const titleAlignment =
		isValidCustomSitesTitleAlignment &&
		titleContentProperties &&
		titleContentProperties.titleLayoutAlignment
			? titleContentProperties.titleLayoutAlignment
			: '';

	const leftRightPaddingValueOffset =
		isPagePreview && leftRightPaddingValue ? leftRightPaddingValue : 0;

	const { height } = usePageHeightWidthObserver(
		isPagePreview,
		leftRightPaddingValueOffset,
		isPresenterMode,
	);

	const smartSpaceOverviewHeight = isSmartSpaceOverview
		? SMART_SPACE_OVERVIEW_FIXED_HEIGHT_FULL_WIDTH
		: undefined;
	const dynamicHeight = isFixedWidthImageOption ? COVER_IMAGE_FIXED_HEIGHT : height;

	// if we are in page preview, we need to show the draft image/position/appearance since we are in edit mode
	// If we are rendered via diff editor and live page, we need to show the draft image/position/appearance since live pages store their cover picture in draft
	const imageId =
		(isPagePreview || useDraftData
			? coverPicture.draft?.value.id
			: coverPicture.published?.value.id) || '';
	const position =
		(isPagePreview || useDraftData
			? coverPicture.draft?.value.position
			: coverPicture.published?.value.position) || '';
	const objectPosition = `center ${position}%`;
	const appearance = isPagePreview ? contentAppearance.draft : contentAppearance.published;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	// using imageId as key since the query gets outdated information and we need to force a re-render after it gets the
	// correct info
	// Check if the cover picture is from an allowed external CDN (Unsplash, dam-cdn, or wac-cdn)
	const isAllowedExternalCoverPicture = !!imageId && isAllowedExternalCoverPictureUrl(imageId);

	const currentPageMode = getPageMode(
		isPagePreview,
		isSpaceOverview,
		isPresenterMode,
		isEmbeddedConfluence,
		isDiffEditor,
	);

	const createViewHeaderImageAnalyticsEvent = useCallback(
		(
			isUnsplash: boolean,
			appearance: string,
			pageMode: string,
			pageTitleAlignment: string,
			isFixedWidthCoverImage: boolean,
		) => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'viewed',
					actionSubject: 'HeaderImage',
					actionSubjectId: 'viewPageHeaderImage',
					source: 'viewPageHeaderImage',
					attributes: {
						headerImageType: isUnsplash ? 'unsplash' : 'user-uploaded',
						contentAppearance: appearance,
						pageMode,
						pageTitleAlignment,
						coverImageWidth: isFixedWidthCoverImage ? 'fixed-width' : 'full-width',
						isAIGenerationEnabled: isExperimentEnabled(
							'cc_page_experiences_ai_header_image_generation',
						),
						isAIFCAutoGeneratedImageEnabled: isExperimentEnabled(
							'cc_page_experiences_aifc_auto_generated_header_img',
						),
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	useEffect(() => {
		createViewHeaderImageAnalyticsEvent(
			isAllowedExternalCoverPicture,
			appearance,
			currentPageMode,
			titleAlignment,
			isFixedWidthImageOption,
		);
	}, [
		isAllowedExternalCoverPicture,
		appearance,
		currentPageMode,
		titleAlignment,
		isFixedWidthImageOption,
		createViewHeaderImageAnalyticsEvent,
	]);

	// Setup media config
	useEffect(() => {
		let isMounted = true;
		setMediaConfig(null);

		void createRendererMediaProvider({
			contentId,
			isExternalShare,
		}).then(({ viewMediaClientConfig }) => {
			if (isMounted) {
				setMediaConfig({
					collection: `contentId-${contentId}`,
					authProvider: viewMediaClientConfig.authProvider,
				});
			}
		});

		return () => {
			isMounted = false;
		};
	}, [contentId, isExternalShare]);

	// return placeholder if there is a custom upload
	if (!isMediaConfigGateReady || (!imageId && !isAllowedExternalCoverPicture)) {
		return (
			<IdentifierWrapper
				isPagePreview={isPagePreview}
				isEmbeddedConfluence={isEmbeddedConfluence}
				isFixedWidthImageOption={isFixedWidthImageOption}
				isSmartSpaceOverview={isSmartSpaceOverview}
			>
				<LoadingDiv
					isPagePreview={isPagePreview}
					isSpaceOverview={isSpaceOverview}
					isFixedWidthImageOption={isFixedWidthImageOption}
					smartSpaceOverviewHeight={smartSpaceOverviewHeight}
					isObjectHeaderEnabled={fg('confluence_frontend_content_wrapper')}
				/>
			</IdentifierWrapper>
		);
	}

	if (isAllowedExternalCoverPicture) {
		return (
			<IdentifierWrapper
				isPagePreview={isPagePreview}
				isEmbeddedConfluence={isEmbeddedConfluence}
				isFixedWidthImageOption={isFixedWidthImageOption}
				isSmartSpaceOverview={isSmartSpaceOverview}
			>
				<AllowedExternalWrapper key={imageId}>
					<AllowedExternalLoadingDiv
						isPagePreview={isPagePreview}
						hasAllowedExternalImageLoaded={hasAllowedExternalImageLoaded}
						isSpaceOverview={isSpaceOverview}
						isEmbeddedConfluence={isEmbeddedConfluence}
						isFixedWidthImageOption={isFixedWidthImageOption}
						smartSpaceOverviewHeight={smartSpaceOverviewHeight}
						isObjectHeaderEnabled={fg('confluence_frontend_content_wrapper')}
					/>
					<ImageWrapper
						data-testid="unsplash-cover-picture"
						data-vc="unsplash-cover-picture-renderer"
						// need this empty alt tag for decorative images
						alt=""
						isPagePreview={isPagePreview}
						src={imageId}
						objectPosition={objectPosition}
						onLoad={() => {
							setHasAllowedExternalImageLoaded(true);
						}}
						isSpaceOverview={isSpaceOverview}
						isRoundedCorners={isSmartSpaceOverview}
						hasEmoji={hasEmoji}
						dynamicCoverImageHeight={undefined}
						presenterModeHeight={isPresenterMode ? dynamicHeight : undefined}
						isPresenterMode={isPresenterMode}
						isFixedWidthImageOption={isFixedWidthImageOption}
						smartSpaceOverviewHeight={smartSpaceOverviewHeight}
						isFullWidthImageOption={isFullWidthImageOption}
						isObjectHeaderEnabled={fg('confluence_frontend_content_wrapper')}
					/>
				</AllowedExternalWrapper>
			</IdentifierWrapper>
		);
	} else if (isMediaConfigGateReady && mediaConfigGated) {
		return (
			<MediaImage
				key={imageId}
				identifier={{
					mediaItemType: 'file',
					id: imageId,
					collectionName: isPageSSRed ? `contentId-${contentId}` : mediaConfig?.collection,
				}}
				mediaClientConfig={mediaConfigGated}
				apiConfig={{
					allowAnimated: true,
					mode: 'full-fit' as const,
					upscale: false,
				}}
				ssr={isPageSSRed ? ssrConfig?.mode : undefined}
			>
				{({ data, loading }) => {
					if (loading) {
						return (
							<IdentifierWrapper
								isPagePreview={isPagePreview}
								isEmbeddedConfluence={isEmbeddedConfluence}
								isFixedWidthImageOption={isFixedWidthImageOption}
								isSmartSpaceOverview={isSmartSpaceOverview}
							>
								<LoadingDiv
									isPagePreview={isPagePreview}
									isSpaceOverview={isSpaceOverview}
									isFixedWidthImageOption={isFixedWidthImageOption}
									smartSpaceOverviewHeight={smartSpaceOverviewHeight}
									isObjectHeaderEnabled={fg('confluence_frontend_content_wrapper')}
								/>
							</IdentifierWrapper>
						);
					}
					if (!data) {
						return null;
					}
					return (
						<IdentifierWrapper
							isPagePreview={isPagePreview}
							isEmbeddedConfluence={isEmbeddedConfluence}
							isFixedWidthImageOption={isFixedWidthImageOption}
							isSmartSpaceOverview={isSmartSpaceOverview}
						>
							<ImageWrapper
								isPagePreview={isPagePreview}
								src={data.src}
								objectPosition={objectPosition}
								isSpaceOverview={isSpaceOverview}
								isRoundedCorners={isSmartSpaceOverview}
								hasEmoji={hasEmoji}
								dynamicCoverImageHeight={undefined}
								presenterModeHeight={isPresenterMode ? dynamicHeight : undefined}
								isPresenterMode={isPresenterMode}
								isFixedWidthImageOption={isFixedWidthImageOption}
								smartSpaceOverviewHeight={smartSpaceOverviewHeight}
								isFullWidthImageOption={isFullWidthImageOption}
								isObjectHeaderEnabled={fg('confluence_frontend_content_wrapper')}
								alt=""
							/>
						</IdentifierWrapper>
					);
				}}
			</MediaImage>
		);
	}
};

export const RendererPageCoverPicture = withTransparentErrorBoundary({
	attribution: Attribution.PAGE_EXTENSIONS,
})(RendererPageCoverPictureComponent);
